import React, { useRef, useEffect } from 'react';
import VisitingObjectsSearch from './index';
import { autoplayPace as paceTimings, createLiveDemoAutoplay, prefersReducedMotion } from './demo-autoplay';
import { createDemoTransport, DEMO_TYPE_INPUT, DEMO_CLEAR_INPUT, DEMO_HALT } from './demo-transport';

/**
 * The landing-page search demo (plan #4906). Autoplay types scripted queries
 * into the REAL widget, which runs real searches against the seeded demo
 * instance: results come from the live pipeline with real highlighting, the
 * server caches them (long demo TTL, cache hits after the first cycle),
 * suppresses demo analytics server-side, and rate-bounds the instance. The
 * bundled fixture layer this replaces is deleted.
 *
 * Autoplay posture (kept from #7964): IntersectionObserver-gated (0.3),
 * suppressed under prefers-reduced-motion and hidden tabs, and once the user
 * takes over it never restarts, so it cannot resume traffic behind a
 * visitor's back.
 */
/** Fired as each autoplay term starts (item i22509); detail {query, index, durationMs}. */
export const DEMO_QUERY_EVENT = 'vo-search-demo-query';

const DemoSearch = ({
  clientPublicToken,
  demoMode = true,
  onUserInteraction = null,
  demoTransport = null,
  // The film's autoplay pins (item i22492): a pace name, and the script index a
  // fresh controller opens on. Null keeps the standard pace and the rotation.
  autoplayPace = null,
  autoplayStart = null,
  ...props
}) => {
  const demoComponentRef = useRef(null);
  // The autoplay controller lives in a ref so the IntersectionObserver and
  // visibility callbacks -- stable closures -- always drive the live one.
  const autoplayRef = useRef(null);
  // This demo's own event channel (item #8838). One per DemoSearch, resolved
  // once and never replaced, so N demos on a page type into N inputs rather
  // than all of them typing into all of them.
  //
  // The embed supplies one when it has a container to associate it with (item
  // #9013): a fallback swap has to reach the demo it is hiding, and the embed
  // is the only place that knows which demo that is. Standing alone, this
  // creates its own.
  const transportRef = useRef(null);
  if (transportRef.current === null) transportRef.current = demoTransport || createDemoTransport();
  const transport = transportRef.current;

  // Wire the demo event plumbing: the widget's user-interaction signals end
  // autoplay, and the page-wide signals (rate limit, halt) hold or stop it.
  // Typing itself does not pass through here -- it goes down the transport
  // (#8838), which is the only path that can reach the input's shadow root.
  useEffect(() => {
    if (!demoMode || typeof window === 'undefined') return undefined;

    const takeOver = (interactionType, clearAfter) => {
      if (onUserInteraction) {
        onUserInteraction(interactionType);
      }
      if (autoplayRef.current) {
        autoplayRef.current.handleInteraction();
      }
      if (clearAfter) {
        setTimeout(() => transport.emit(DEMO_CLEAR_INPUT), 100);
      }
    };

    const onFocus = () => takeOver('focus', true);
    const onTyping = () => takeOver('typing', true);
    const onScroll = () => takeOver('scroll', false);
    const onClick = () => takeOver('click', false);
    // The embed sends this after swapping THIS demo to its screenshot fallback;
    // autoplay must never keep firing into a hidden tree. It arrives on the
    // transport (item #9013) because it names one demo: the handler is the
    // permanent takeover path, so a page-wide send ended every demo on the page
    // whenever any one panel failed its health check.
    const onHalt = () => {
      if (autoplayRef.current) {
        autoplayRef.current.handleInteraction();
      }
    };
    // Server back-pressure (#8364): hold the script for the Retry-After
    // window rather than typing into a 429 wall. Unlike a halt this is
    // temporary -- autoplay resumes once the window passes.
    const onRateLimited = (e) => {
      if (autoplayRef.current) {
        autoplayRef.current.pauseFor((e.detail?.retryAfterSeconds || 0) * 1000);
      }
    };

    const offHalt = transport.on(DEMO_HALT, onHalt);

    window.addEventListener('vo-search-rate-limited', onRateLimited);
    // Still listenable on window for an external sender; the embed is no longer
    // one (item #9013).
    window.addEventListener('vo-search-demo-halt', onHalt);
    window.addEventListener('vo-search-user-focus', onFocus);
    window.addEventListener('vo-search-user-typing', onTyping);
    window.addEventListener('vo-search-user-scroll', onScroll);
    window.addEventListener('vo-search-user-click', onClick);

    return () => {
      offHalt();
      window.removeEventListener('vo-search-rate-limited', onRateLimited);
      window.removeEventListener('vo-search-demo-halt', onHalt);
      window.removeEventListener('vo-search-user-focus', onFocus);
      window.removeEventListener('vo-search-user-typing', onTyping);
      window.removeEventListener('vo-search-user-scroll', onScroll);
      window.removeEventListener('vo-search-user-click', onClick);
    };
  }, [demoMode, onUserInteraction]);

  // Autoplay lifecycle: in-view starts it, out-of-view and hidden-tab pause
  // it, reduced-motion suppresses it entirely, user takeover ends it, and it
  // only ever runs while a surface is mounted to receive the script.
  useEffect(() => {
    if (!demoMode || typeof window === 'undefined') return undefined;
    if (prefersReducedMotion()) return undefined;

    const el = demoComponentRef.current;
    const willObserve = typeof IntersectionObserver !== 'undefined' && !!el;
    // Without an observer nothing ever says this demo is off screen, so the
    // view gate opens; with one, it stays shut until the observer reports.
    let inView = !willObserve;

    const autoplay = createLiveDemoAutoplay({
      ...paceTimings(autoplayPace),
      ...(autoplayStart === null ? {} : { startIndex: autoplayStart }),
      typeQuery: (partial) => {
        transport.emit(DEMO_TYPE_INPUT, { text: partial });
      },
      clearBetween: () => {
        transport.emit(DEMO_CLEAR_INPUT);
      },
      isHidden: () => typeof document !== 'undefined' && document.hidden,
      isInView: () => inView,
      // A surface exists exactly when a SearchInput is subscribed to this
      // demo's transport. An overlay mode mounts its input on open, so before
      // that the script would advance into nothing and the visitor who opened
      // the popup would join mid-word (item #8842).
      hasSurface: () => transport.hasSubscriber(DEMO_TYPE_INPUT),
      // Each term's start, for a page that switches looks with it (item
      // i22509). Composed and bubbling from this demo's own tree, so it leaves
      // the shadow root and reaches only the page element that holds this demo.
      onQueryStart: (query, index, durationMs) => {
        demoComponentRef.current?.dispatchEvent(
          new CustomEvent(DEMO_QUERY_EVENT, { detail: { query, index, durationMs }, bubbles: true, composed: true }),
        );
      },
    });
    autoplayRef.current = autoplay;

    // The surface appearing and disappearing is the overlay opening and
    // closing. Restarting begins a whole query rather than resuming a
    // half-typed one, which is what makes the open read as a beginning.
    const offSurfaceChange = transport.onSubscriberChange((type) => {
      if (type !== DEMO_TYPE_INPUT) return;
      if (transport.hasSubscriber(type)) {
        autoplay.start();
      } else {
        autoplay.stop();
      }
    });

    const handleVisibility = () => {
      if (document.hidden) {
        autoplay.stop();
      } else {
        autoplay.start();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    let observer = null;
    let fallbackTimer = null;

    if (!willObserve) {
      // No observer available: fall back to a one-shot delayed start.
      fallbackTimer = setTimeout(() => autoplay.start(), 1000);
    } else {
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            inView = entry.isIntersecting;
            if (entry.isIntersecting) {
              autoplay.start();
            } else {
              autoplay.stop();
            }
          }
        },
        { threshold: 0.3 },
      );
      observer.observe(el);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      offSurfaceChange();
      if (observer) observer.disconnect();
      if (fallbackTimer) clearTimeout(fallbackTimer);
      autoplay.stop();
      autoplayRef.current = null;
    };
  }, [demoMode, autoplayPace, autoplayStart]);

  return (
    <div ref={demoComponentRef} style={{ position: 'relative' }}>
      <VisitingObjectsSearch
        clientPublicToken={clientPublicToken}
        demoMode={demoMode}
        demoTransport={transport}
        ui={{
          // Render mode is authoritative per-instance server config (#5414),
          // configured via the config endpoint / dashboard; the demo consumes
          // it and never forces a client-side mode. Only demo behavior is
          // overridden here: no URL rewrite, no focus steal.
          pushState: false, // Disable URL state management to prevent browser history pollution
          autoFocus: false, // Disable auto focus to prevent interference with demo
          ...props.ui
        }}
        {...props}
      />
    </div>
  );
};

export default DemoSearch;
export { VisitingObjectsSearch, VisitingObjectsSearch as DemoVisitingObjectsSearch };
